import React from "react";
import { motion } from "framer-motion";

/**
 * Premium 3D-style "Coming Soon" placeholder — pure SVG + CSS.
 * Each variant renders a category-specific scene behind a shared
 * metallic-gold "COMING SOON" wordmark and glass badge.
 */

const SCENES = {
  school: (
    <g>
      {/* floating books stack */}
      <g transform="translate(340,180) rotate(-6)">
        <rect x="-70" y="-8" width="140" height="16" rx="2" fill="#c9a534" opacity="0.85" />
        <rect x="-64" y="-24" width="128" height="16" rx="2" fill="#f0d574" opacity="0.7" />
        <rect x="-60" y="-40" width="120" height="16" rx="2" fill="#8b6d1e" opacity="0.7" />
      </g>
      {/* graduation cap outline */}
      <g transform="translate(140,190)" stroke="#d4af37" strokeOpacity="0.55" fill="none" strokeWidth="1.4">
        <path d="M -50 0 L 0 -22 L 50 0 L 0 22 Z" />
        <path d="M -30 8 L -30 30 Q 0 46 30 30 L 30 8" />
      </g>
      {/* orbit ring */}
      <circle cx="260" cy="200" r="150" stroke="#d4af37" strokeOpacity="0.12" fill="none" />
    </g>
  ),
  college: (
    <g>
      {/* pillars */}
      {[100, 180, 260, 340, 420].map((x) => (
        <rect key={x} x={x - 12} y="180" width="24" height="140" fill="url(#gold-vert)" opacity="0.65" />
      ))}
      <rect x="80" y="164" width="360" height="14" fill="#d4af37" opacity="0.75" />
      <path d="M 80 164 L 260 100 L 440 164 Z" stroke="#d4af37" strokeOpacity="0.55" fill="none" strokeWidth="1.5" />
      <circle cx="260" cy="130" r="6" fill="#f0d574" />
    </g>
  ),
  gym: (
    <g>
      {/* dumbbell */}
      <g transform="translate(260,220)" stroke="#d4af37" fill="none" strokeWidth="2">
        <rect x="-100" y="-10" width="200" height="20" rx="4" fill="#c9a534" opacity="0.85" stroke="none" />
        <rect x="-140" y="-32" width="30" height="64" rx="6" fill="#f0d574" stroke="none" />
        <rect x="110" y="-32" width="30" height="64" rx="6" fill="#f0d574" stroke="none" />
        <rect x="-160" y="-42" width="18" height="84" rx="6" fill="#8b6d1e" stroke="none" />
        <rect x="142" y="-42" width="18" height="84" rx="6" fill="#8b6d1e" stroke="none" />
      </g>
      {/* energy sparks */}
      {[0, 60, 120, 240, 300].map((a) => (
        <circle key={a} cx={260 + Math.cos((a * Math.PI) / 180) * 180} cy={220 + Math.sin((a * Math.PI) / 180) * 100} r="1.6" fill="#f0d574" />
      ))}
    </g>
  ),
  restaurant: (
    <g>
      {/* plate + cutlery */}
      <ellipse cx="260" cy="240" rx="150" ry="30" fill="#0a0a0a" stroke="#d4af37" strokeOpacity="0.5" />
      <circle cx="260" cy="230" r="80" fill="url(#gold-radial)" opacity="0.35" stroke="#d4af37" strokeOpacity="0.5" />
      <rect x="150" y="215" width="70" height="4" rx="2" fill="#d4af37" opacity="0.65" />
      <rect x="300" y="215" width="70" height="4" rx="2" fill="#d4af37" opacity="0.65" />
      <path d="M 380 195 L 380 240 M 372 195 L 388 195" stroke="#d4af37" strokeOpacity="0.65" strokeWidth="1.5" />
    </g>
  ),
  cafe: (
    <g>
      {/* coffee cup */}
      <g transform="translate(230,200)" stroke="#d4af37" strokeOpacity="0.7" fill="none" strokeWidth="1.8">
        <path d="M 0 0 L 0 90 Q 0 110 30 110 L 90 110 Q 120 110 120 90 L 120 0 Z" fill="#0e0e10" />
        <path d="M 120 20 Q 150 20 150 50 Q 150 80 120 80" />
        {/* steam */}
        <path d="M 30 -30 Q 40 -10 30 5" strokeOpacity="0.4" />
        <path d="M 60 -40 Q 70 -20 60 -5" strokeOpacity="0.4" />
        <path d="M 90 -30 Q 100 -10 90 5" strokeOpacity="0.4" />
      </g>
      <circle cx="290" cy="240" r="12" fill="#c9a534" opacity="0.6" />
    </g>
  ),
  corporate: (
    <g>
      {/* skyline */}
      <g fill="url(#gold-vert)" opacity="0.6">
        <rect x="90" y="180" width="40" height="160" />
        <rect x="140" y="140" width="50" height="200" />
        <rect x="200" y="100" width="60" height="240" />
        <rect x="270" y="150" width="50" height="190" />
        <rect x="330" y="180" width="40" height="160" />
        <rect x="380" y="140" width="50" height="200" />
      </g>
      {/* network grid overlay */}
      <g stroke="#d4af37" strokeOpacity="0.15">
        {[110, 165, 230, 295, 350, 405].map((x) => (
          <line key={`v${x}`} x1={x} y1="80" x2={x} y2="340" />
        ))}
        {[110, 170, 230, 290].map((y) => (
          <line key={`h${y}`} x1="80" y1={y} x2="440" y2={y} />
        ))}
      </g>
      {/* golden node */}
      <circle cx="230" cy="170" r="6" fill="#f0d574" />
      <circle cx="230" cy="170" r="14" stroke="#f0d574" strokeOpacity="0.3" fill="none" />
    </g>
  ),
  healthcare: (
    <g>
      {/* medical cross + glass panel */}
      <rect x="160" y="120" width="200" height="200" rx="16" fill="#0e0e10" stroke="#d4af37" strokeOpacity="0.4" />
      <g transform="translate(260,220)">
        <rect x="-20" y="-60" width="40" height="120" rx="8" fill="url(#gold-radial)" />
        <rect x="-60" y="-20" width="120" height="40" rx="8" fill="url(#gold-radial)" />
      </g>
      {/* orbit */}
      <circle cx="260" cy="220" r="130" stroke="#d4af37" strokeOpacity="0.14" fill="none" strokeDasharray="4 6" />
    </g>
  ),
  boutique: (
    <g>
      {/* hanger + fabric drape */}
      <g transform="translate(260,150)" stroke="#d4af37" strokeOpacity="0.7" fill="none" strokeWidth="1.6">
        <path d="M -60 20 Q 0 -20 60 20" />
        <path d="M 0 20 L 0 4 Q 0 -4 10 -4 Q 20 -4 20 4" />
      </g>
      <path
        d="M 200 170 L 130 340 L 390 340 L 320 170 Z"
        fill="url(#gold-vert)"
        opacity="0.55"
      />
      <path d="M 260 170 L 260 340" stroke="#d4af37" strokeOpacity="0.5" />
    </g>
  ),
};

const PortfolioPlaceholder = ({ variant = "corporate", label = "COMING SOON", subtitle }) => {
  const scene = SCENES[variant] || SCENES.corporate;

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#080809]">
      {/* Deep radial background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(212,175,55,0.12) 0%, transparent 55%), radial-gradient(ellipse at 20% 100%, rgba(180,140,40,0.08) 0%, transparent 55%), #060607",
        }}
      />

      {/* Scene layer */}
      <svg
        viewBox="0 0 520 400"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="gold-vert" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f0d574" />
            <stop offset="60%" stopColor="#c9a534" />
            <stop offset="100%" stopColor="#5c4813" />
          </linearGradient>
          <radialGradient id="gold-radial" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f0d574" stopOpacity="0.8" />
            <stop offset="80%" stopColor="#d4af37" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
          </radialGradient>
          <filter id="soft-glow">
            <feGaussianBlur stdDeviation="4" />
          </filter>
        </defs>
        {scene}
      </svg>

      {/* Grid + particles overlay */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(212,175,55,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.05) 1px, transparent 1px)",
          backgroundSize: "36px 36px",
          maskImage:
            "radial-gradient(circle at 50% 50%, black, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(circle at 50% 50%, black, transparent 75%)",
        }}
      />

      {/* Floating particles */}
      <div className="pointer-events-none absolute inset-0">
        {Array.from({ length: 14 }).map((_, i) => {
          const left = (i * 37) % 100;
          const top = (i * 53) % 100;
          const size = 1 + ((i * 7) % 3);
          const delay = (i * 0.4) % 3;
          return (
            <motion.span
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: size,
                height: size,
                background:
                  "radial-gradient(circle, rgba(246,216,114,0.9), rgba(212,175,55,0))",
              }}
              animate={{ y: [0, -20, 0], opacity: [0.4, 1, 0.4] }}
              transition={{
                duration: 5 + (i % 4),
                delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </div>

      {/* Center wordmark */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
        <div className="relative">
          <div
            aria-hidden
            className="absolute inset-0 blur-2xl"
            style={{
              background:
                "radial-gradient(circle, rgba(212,175,55,0.35), transparent 60%)",
            }}
          />
          <h3
            className="relative font-display text-[34px] font-semibold leading-none tracking-[-0.03em] sm:text-[46px]"
            style={{
              background:
                "linear-gradient(180deg, #fbe89a 0%, #d4af37 45%, #7a5f18 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 6px 18px rgba(212,175,55,0.35))",
            }}
          >
            {label}
          </h3>
        </div>
        {subtitle && (
          <p className="mt-3 max-w-[80%] text-[12px] tracking-widest text-zinc-300/85 sm:text-[13px]">
            {subtitle}
          </p>
        )}
      </div>

      {/* Corner badge */}
      <div className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full border border-[var(--gold)]/50 bg-black/60 px-3 py-1.5 backdrop-blur-md shadow-[0_0_18px_-4px_rgba(212,175,55,0.5)]">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--gold)] opacity-60" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--gold-bright)]" />
        </span>
        <span
          className="font-mono-label text-[9px] text-[var(--gold-bright)]"
          style={{ letterSpacing: "0.24em" }}
        >
          IN DEVELOPMENT
        </span>
      </div>

      {/* Corner meta */}
      <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between text-[10.5px]">
        <div>
          <div
            className="font-mono-label text-[9px] text-[var(--gold)]"
            style={{ letterSpacing: "0.28em" }}
          >
            Future Portfolio Project
          </div>
          <div className="mt-1 text-zinc-500">
            This project will be showcased once completed.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPlaceholder;
